import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckCircle2,
    XCircle,
    Loader2,
    Rocket,
    ExternalLink,
    QrCode,
    Download,
    Copy,
    ArrowRight,
    Sparkles,
    Upload,
    Info,
    Pencil,
    AlertTriangle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import PublicationVersions from "@/components/PublicationVersions";
import TemplateGallery from "@/components/TemplateGallery";
import AIProgressIndicator from "@/components/AIProgressIndicator";

/**
 * Maps a validation check key to its "fix" action. Returns `null` when the
 * check is not doctor-fixable in the current status — the row is still shown
 * but rendered non-clickable so we NEVER navigate somewhere that bounces the
 * user out (previous bug: /doctor/review-content didn't exist → catch-all
 * <Route path="*"> sent them to `/`, which read as "kicked out").
 *
 * `to`     — react-router destination (must exist in App.js)
 * `action` — inline handler (upload_photo)
 * `passive`— link is informational only (payment/billing)
 * `ai`     — this row can be self-healed by clicking "Regenerate with AI"
 */
function fixForCheck(key, profileStatus, diagnostic) {
    // If the check comes with a server-side diagnostic, ALWAYS open the
    // inline "Fill this information" dialog on the publish page. The
    // dialog writes to POST /me/content-fill which is permitted in every
    // status from onboarding through published, then auto-triggers a
    // regenerate on success — so the doctor never has to navigate away
    // or wait for an admin to unlock their wizard.
    if (
        diagnostic &&
        Array.isArray(diagnostic.missing_inputs) &&
        diagnostic.missing_inputs.length > 0
    ) {
        return {
            action: "fill_info",
            label: "Fill this information",
            missing_inputs: diagnostic.missing_inputs,
            missing_input_hint: diagnostic.hint,
            check_key: key,
        };
    }
    switch (key) {
        case "profile_photo":
            return { action: "upload_photo", label: "Upload photo" };
        case "seo_title":
        case "content_ready_seo_title":
            return { to: "/doctor/seo?focus=title", label: "Fix SEO title", ai: "seo" };
        case "meta_description":
        case "content_ready_meta_description":
            return { to: "/doctor/seo?focus=meta", label: "Fix meta description", ai: "seo" };
        case "biography":
        case "content_ready_professional_biography":
        case "content_ready_profile_summary":
        case "content_ready_specialization_description":
        case "content_ready_practice_philosophy":
        case "content_ready_mission_vision":
        case "content_ready_page_headings": {
            // Stale-output case (no missing_inputs) — offer BOTH remedies:
            //   primary: Regenerate with AI (fixes stale content)
            //   secondary: Edit/add more info (opens Fill dialog with the
            //   narrative inputs seeded, so doctors who WANT to add more
            //   grounding have an obvious path too).
            // The doctor_review deep-link takes precedence when applicable
            // (that page has richer per-section editors).
            if (profileStatus === "doctor_review") {
                return { to: "/doctor/review?focus=biography", label: "Edit content", ai: "biography" };
            }
            return {
                ai: "biography",
                label: "Regenerate with AI",
                hint_only: true,
                secondary: {
                    action: "fill_info",
                    label: "Edit / add more info",
                    // No server-diagnosed missing_inputs — seed the Fill
                    // dialog with the narrative inputs the AI most needs.
                    missing_inputs: ["biography_notes", "education", "experience"],
                    missing_input_hint:
                        "Add more real biography notes / education / experience so the AI has richer material to work with.",
                    check_key: key,
                },
            };
        }
        case "doctor_name":
            return { to: "/doctor/onboarding?step=1", label: "Set name" };
        case "specialization":
            return { to: "/doctor/onboarding?step=1", label: "Set specialization" };
        case "payment_full":
            return { to: "/doctor/billing", label: "Complete balance payment", passive: true };
        case "admin_approved":
        case "doctor_approved":
        case "ready_status":
            return null;  // handled by workflow, not doctor-clickable
        case "terms":
        case "slug":
            return null;
        default:
            return null;
    }
}

export default function DoctorPublish() {
    const nav = useNavigate();
    const [doctor, setDoctor] = useState(null);
    const [checks, setChecks] = useState([]);
    const [ok, setOk] = useState(false);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [regenBusy, setRegenBusy] = useState(false);
    const [regenStartedAt, setRegenStartedAt] = useState(null);
    const [regenStatus, setRegenStatus] = useState(null); // 'queued' | 'processing' | 'completed' | 'failed'
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    // Inline "Fill this information" dialog state — mirrors the diagnostic
    // shape from /publish/validate.
    const [fillOpen, setFillOpen] = useState(false);
    const [fillMissing, setFillMissing] = useState([]);
    const [fillHint, setFillHint] = useState("");
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsText, setDetailsText] = useState("");
    // Auto-scroll + celebration state — when `ok` flips false→true (all
    // checks just passed after a fill+regenerate chain), we scroll the
    // Publish CTA into view and show a top-of-page success banner so the
    // doctor never wonders "what next?".
    const publishRef = useRef(null);
    const prevOkRef = useRef(false);
    const [justPassed, setJustPassed] = useState(false);

    const load = useCallback(async () => {
        try {
            const [d, v, p] = await Promise.all([
                api.get("/me/doctor"),
                api.get("/publish/validate"),
                api.get("/publish/preview-url"),
            ]);
            setDoctor(d.data);
            setChecks(v.data.checks || []);
            setOk(v.data.ok);
            setPreview(p.data);
        } catch (err) {
            toast.error(errMsg(err, "Failed to load"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Detect the false→true transition on `ok` — when the checklist just
    // turned all-green, celebrate + scroll Publish into view. Also emit a
    // toast so the doctor gets a persistent affordance if they scroll away.
    useEffect(() => {
        const wasOk = prevOkRef.current;
        prevOkRef.current = ok;
        if (!wasOk && ok && doctor?.profile_status === "ready_to_publish") {
            setJustPassed(true);
            toast.success("All checks passed — your profile is ready to publish!", {
                description: "Click Publish now below to take your identity live.",
                duration: 10000,
            });
            // Scroll after paint so the ref exists + layout is stable.
            setTimeout(() => {
                publishRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 350);
        }
        if (!ok) setJustPassed(false);
    }, [ok, doctor?.profile_status]);

    // Re-validate the checklist automatically whenever the doctor returns to
    // this tab (typical flow: click "Fix in SEO" → edit → back → checklist
    // should refresh without a manual reload).
    useEffect(() => {
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", () => {
            if (!document.hidden) load();
        });
        return () => {
            window.removeEventListener("focus", onFocus);
        };
    }, [load]);

    const publish = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/publish/publish", {});
            toast.success("Published! Your profile is live.", {
                description: data.public_url,
                duration: 6000,
            });
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
                detail.errors.forEach((c) => toast.error(c.label || c.key || (typeof c === "string" ? c : "Check failed")));
            } else {
                toast.error(errMsg(err, "Publish failed"));
            }
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("category", "profile_photo");
            await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Profile photo uploaded");
            await load();
        } catch (err) {
            toast.error(errMsg(err, "Upload failed"));
        } finally {
            setUploadingPhoto(false);
        }
    };

    // Ref lets us cancel an in-flight poll if the doctor kicks off a fresh
    // regenerate while the previous one is still polling.
    const pollAbortRef = useRef({ cancelled: false });

    const regenerateAi = async () => {
        // Cancel any prior poll so we never end up with two intervals racing
        // to setRegenBusy(false) and clobbering the button state.
        pollAbortRef.current.cancelled = true;
        pollAbortRef.current = { cancelled: false };
        const myAbort = pollAbortRef.current;

        setRegenBusy(true);
        setRegenStartedAt(Date.now());
        setRegenStatus("queued");
        let jobId = null;
        try {
            const { data } = await api.post("/me/ai/regenerate", {});
            jobId = data.job_id;
            toast.success("AI regeneration started", {
                description: `Job ${jobId?.slice(0, 8) || ""} queued — this usually takes 10–30 seconds`,
                duration: 4000,
            });
        } catch (err) {
            // Backend can reject up-front for two reasons:
            //  (a) insufficient_profile_data — validate_submission failed (missing name / specialization / etc)
            //  (b) ai_input_missing — profile is filled enough to publish but the
            //      narrative sections still have NO grounding input, so a re-run
            //      would just produce another "Information Required".
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object") {
                if (detail.code === "insufficient_profile_data") {
                    const first = Array.isArray(detail.errors) ? detail.errors.slice(0, 3).join(" · ") : "";
                    toast.error("Complete your profile first", {
                        description: first
                            ? `Missing: ${first}. Open the onboarding wizard to fill these fields.`
                            : "Open the onboarding wizard to fill in the missing details.",
                        duration: 8000,
                        action: { label: "Open wizard", onClick: () => nav("/doctor/onboarding") },
                    });
                } else if (detail.code === "ai_input_junk") {
                    // Server detected junk data (short degree, bad year, empty
                    // role/org). Auto-open the Fill dialog with the exact
                    // fields it wants + surface the specific issues.
                    const issues = Array.isArray(detail.quality_issues) ? detail.quality_issues.join(" · ") : "";
                    toast.error("Your profile inputs need cleanup", {
                        description:
                            "Some entries are too short or use implausible years. Add real details below.",
                        duration: 9000,
                        action: {
                            label: "Fix now",
                            onClick: () => {
                                setFillMissing(detail.missing_inputs?.length ? detail.missing_inputs : ["biography_notes", "education", "experience"]);
                                setFillHint(issues || "Please re-enter your education or experience with proper degree/institution/role/organisation names.");
                                setFillOpen(true);
                            },
                        },
                    });
                } else if (detail.code === "ai_input_missing") {
                    const missing = Array.isArray(detail.missing_inputs) ? detail.missing_inputs.join(", ") : "";
                    toast.error("AI needs more input to write a biography", {
                        description: detail.message + (missing ? ` (missing: ${missing})` : ""),
                        duration: 9000,
                        action: {
                            label: "Fill this information",
                            onClick: () => {
                                setFillMissing(detail.missing_inputs?.length ? detail.missing_inputs : ["biography_notes", "education", "experience"]);
                                setFillHint(detail.message || "Add biography notes, education, or experience so the AI has something factual to summarise.");
                                setFillOpen(true);
                            },
                        },
                    });
                } else {
                    toast.error(errMsg(err, "Regenerate failed"));
                }
            } else {
                toast.error(errMsg(err, "Regenerate failed"));
            }
            setRegenBusy(false);
            return;
        }

        // Poll the AI job endpoint every 2.5s (max 60s). Stop early on
        // completed/failed. IMPORTANT: keep regenBusy=true until the poll
        // terminates so the button spinner stays visible the WHOLE time,
        // not just for the 200ms enqueue call.
        const pollUntil = async () => {
            const startedAt = Date.now();
            const maxMs = 60000;
            while (!myAbort.cancelled && Date.now() - startedAt < maxMs) {
                await new Promise((r) => setTimeout(r, 2500));
                if (myAbort.cancelled) return;
                let job;
                try {
                    job = (await api.get(`/ai/jobs/${jobId}`)).data;
                } catch (_e) {
                    continue;   // transient — try again next tick
                }
                // Reflect processing/queued phase in the visible indicator.
                if (job.status === "queued" || job.status === "processing") {
                    setRegenStatus(job.status);
                }
                if (job.status === "completed") {
                    setRegenStatus("completed");
                    await load();
                    // The sanitizer records `warnings` when it forces a field
                    // to Information Required. Surface the first one as a
                    // targeted next-step for the doctor instead of the old
                    // silent-success toast that never fired.
                    const forced = (job.warnings || []).filter((w) =>
                        w.includes("professional_biography") || w.includes("profile_summary")
                    );
                    if (forced.length) {
                        toast.warning("AI could not fill every section", {
                            description:
                                "The biography / summary came back as 'Information Required'. Add more detail (biography notes, education or experience) and try again.",
                            duration: 10000,
                            action: {
                                label: "Open wizard",
                                onClick: () => nav("/doctor/onboarding?step=18"),
                            },
                        });
                    } else {
                        toast.success("AI content regenerated", {
                            description: "Fresh biography and summary saved. Publishing checklist refreshed.",
                            duration: 5000,
                        });
                    }
                    setRegenBusy(false);
                    return;
                }
                if (job.status === "failed") {
                    setRegenStatus("failed");
                    // Friendly short message — the raw ValueError (which may
                    // contain "First 500 chars: ...") is hidden entirely for
                    // the "all placeholders" refusal, since we already know
                    // exactly what to tell the doctor: fill more input. For
                    // other failures, expose it via "Show technical details".
                    const raw = job.error || "The AI provider returned an error.";
                    const isPlaceholderRefusal = raw.includes("Information Required placeholders");
                    if (isPlaceholderRefusal) {
                        toast.error(
                            "AI could not generate content — your profile information appears incomplete or unclear.",
                            {
                                description:
                                    "Click 'Fill this information' on any red row below to add real biography, education, or experience details.",
                                duration: 12000,
                                action: {
                                    label: "Open Fill dialog",
                                    onClick: () => {
                                        // Open the Fill dialog seeded with the
                                        // narrative inputs the AI needs. The
                                        // parent computes real missing inputs
                                        // once, on demand.
                                        setFillMissing(["biography_notes", "education", "experience"]);
                                        setFillHint(
                                            "Add a real 2–3 sentence biography note OR one education entry (degree + institution) OR one experience entry (role + organisation). The AI cannot proceed without factual grounding.",
                                        );
                                        setFillOpen(true);
                                    },
                                },
                            },
                        );
                    } else {
                        toast.error(
                            "AI job failed. Please try again in a minute — if it keeps failing, use 'Fill this information' to add more input.",
                            {
                                description: "Click 'Show technical details' to see the exact error.",
                                duration: 12000,
                                action: {
                                    label: "Show technical details",
                                    onClick: () => {
                                        setDetailsText(raw);
                                        setDetailsOpen(true);
                                    },
                                },
                            },
                        );
                    }
                    setRegenBusy(false);
                    return;
                }
                // Still queued/processing → loop again
            }
            if (!myAbort.cancelled) {
                toast("AI is taking longer than expected", {
                    description:
                        "The job is still running. Refresh in a minute — the checklist will update once it completes.",
                    duration: 8000,
                });
                setRegenBusy(false);
            }
        };
        pollUntil();
    };

    // Cancel outstanding poll on unmount so state doesn't update after we're gone.
    useEffect(() => {
        return () => {
            pollAbortRef.current.cancelled = true;
        };
    }, []);

    const canPublish = ok && doctor?.profile_status === "ready_to_publish";
    const isPublished = doctor?.profile_status === "published";
    const isSuspended = doctor?.profile_status === "suspended";

    const copy = async (v) => {
        try {
            await navigator.clipboard.writeText(v);
            toast.success("Copied");
        } catch (_e) {
            toast.error("Copy failed");
        }
    };

    return (
        <DoctorShell doctor={doctor}>
            <div className="animate-fade-up">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                    Publish
                </div>
                <h1 className="mt-1 text-3xl font-bold text-[#123B5D]">
                    Take your digital identity live
                </h1>
                <p className="mt-1 text-sm text-[#17212B]/60">
                    Confirm the checklist, pick a template, and publish your public profile at{" "}
                    <span className="font-mono text-[#0F766E]">
                        {preview?.public_url || "/dr/<your-slug>"}
                    </span>
                    .
                </p>

                {isPublished ? (
                    <div className="mt-6 ddi-card p-5 border-[#15803D]/40 bg-[#15803D]/5 flex flex-wrap items-start justify-between gap-3" data-testid="publish-live-panel">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[#15803D] font-semibold">
                                Live
                            </div>
                            <div className="mt-1 text-base font-bold text-[#123B5D]">
                                Your profile is published
                            </div>
                            <div className="mt-2 text-sm">
                                <a
                                    href={preview?.public_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#0F766E] font-semibold hover:underline inline-flex items-center gap-1"
                                    data-testid="publish-live-url"
                                >
                                    {preview?.public_url} <ExternalLink size={12} />
                                </a>
                                <button
                                    className="ml-3 text-[#17212B]/60 hover:text-[#123B5D] inline-flex items-center gap-1"
                                    onClick={() => copy(preview?.public_url || "")}
                                >
                                    <Copy size={12} /> Copy
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <a
                                href={preview?.qr_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download="qr.png"
                                className="ddi-outline-btn text-sm inline-flex items-center gap-1"
                                data-testid="publish-qr-download"
                            >
                                <Download size={14} /> QR PNG
                            </a>
                            <a
                                href={preview?.card_html_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ddi-outline-btn text-sm inline-flex items-center gap-1"
                                data-testid="publish-card-link"
                            >
                                <QrCode size={14} /> Visiting card
                            </a>
                        </div>
                    </div>
                ) : null}

                {isSuspended ? (
                    <div className="mt-6 ddi-card p-5 border-[#DC2626]/40 bg-[#DC2626]/5" data-testid="publish-suspended-panel">
                        <div className="text-xs uppercase tracking-widest text-[#DC2626] font-semibold">
                            Suspended
                        </div>
                        <div className="mt-1 text-sm text-[#17212B]/70">
                            Your public page is temporarily offline. Contact the platform admin to republish.
                        </div>
                    </div>
                ) : null}

                {/* Success banner — appears when all checks just turned green,
                    so the doctor can't miss the next step. Auto-scrolled Publish
                    CTA is rendered below in the Publish section. */}
                {ok && !isPublished && doctor?.profile_status === "ready_to_publish" ? (
                    <div
                        className="mt-6 ddi-card p-5 border-[#15803D]/40 bg-[#15803D]/5 flex items-start justify-between gap-4 animate-pulse-once"
                        data-testid="publish-ready-banner"
                    >
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[#15803D] font-semibold">
                                ✓ All checks passed
                            </div>
                            <div className="mt-1 text-base font-bold text-[#123B5D]">
                                Your profile is ready to publish
                            </div>
                            <div className="mt-1 text-sm text-[#17212B]/70">
                                One click and your professional page goes live at{" "}
                                <span className="font-mono text-[#0F766E]">{preview?.public_url}</span>.
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                publishRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                            }}
                            className="ddi-teal-btn text-sm inline-flex items-center gap-1.5 flex-shrink-0"
                            data-testid="publish-scroll-to-cta"
                        >
                            Go to Publish <ArrowRight size={14} />
                        </button>
                    </div>
                ) : null}

                {/* Validation checklist */}
                <section className="mt-8">
                    <div className="flex items-baseline justify-between">
                        <h2 className="text-base font-semibold text-[#123B5D]">
                            Publishing checklist
                        </h2>
                        {/* Only show the top-level "Regenerate with AI" pill for
                            content checks whose real remedy is a re-run (i.e.
                            they lack a diagnostic.missing_inputs pointing to
                            fillable rows). If every failing content row has
                            missing_inputs, filling is the remedy — hide the
                            stale AI pill to avoid the doomed loop the user hit. */}
                        {checks.some((c) => {
                            if (c.passed) return false;
                            const isContent = c.key.startsWith("content_ready_") || ["biography", "seo_title", "meta_description"].includes(c.key);
                            if (!isContent) return false;
                            const hasFillableRemedy = Array.isArray(c.diagnostic?.missing_inputs) && c.diagnostic.missing_inputs.length > 0;
                            return !hasFillableRemedy;
                        }) && (
                            <button
                                onClick={regenerateAi}
                                disabled={regenBusy}
                                className="text-xs font-semibold text-[#0F766E] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid="checklist-regenerate-ai"
                            >
                                <Sparkles size={12} className={regenBusy ? "animate-pulse" : ""} />
                                {regenBusy ? "Regenerating…" : "Regenerate with AI"}
                            </button>
                        )}
                    </div>
                    {(regenBusy || regenStatus === "completed" || regenStatus === "failed") && (
                        <div className="mt-3">
                            <AIProgressIndicator
                                status={regenStatus || "processing"}
                                startedAt={regenStartedAt}
                                label={
                                    regenStatus === "completed"
                                        ? "AI regeneration complete"
                                        : regenStatus === "failed"
                                            ? "AI regeneration failed"
                                            : "Regenerating biography & summary"
                                }
                                sub={
                                    regenStatus === "completed"
                                        ? "Checklist refreshed with the new AI output — review the greens below."
                                        : regenStatus === "failed"
                                            ? "See the toast above for next-step guidance."
                                            : "Grounded on your onboarding data — this typically takes 15–45 seconds."
                                }
                            />
                        </div>
                    )}
                    <div className="mt-3 ddi-card p-2" data-testid="publish-checklist">
                        <ul className="divide-y divide-[#123B5D]/5">
                            {checks.map((c) => {
                                const fix = c.passed ? null : fixForCheck(c.key, doctor?.profile_status, c.diagnostic);
                                const clickable = !!fix && !fix.passive;
                                const hint = !c.passed && (fix?.missing_input_hint || c.diagnostic?.hint);
                                const inner = (
                                    <div className="py-3 px-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {c.passed ? (
                                                    <CheckCircle2 size={16} className="text-[#15803D] flex-shrink-0" />
                                                ) : (
                                                    <XCircle size={16} className="text-[#DC2626] flex-shrink-0" />
                                                )}
                                                <span className={c.passed ? "text-[#17212B] text-sm" : "text-[#DC2626] text-sm font-medium"}>
                                                    {c.label}
                                                </span>
                                                {c.message && !c.passed ? (
                                                    <span className="text-xs text-[#17212B]/50 truncate">— {c.message}</span>
                                                ) : null}
                                            </div>
                                            {!c.passed && fix ? (
                                                <span className={`text-xs font-semibold inline-flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${fix.passive ? "text-[#D97706]" : "text-[#0F766E]"}`}>
                                                    {fix.label || "Fix"}
                                                    {fix.passive ? null : <ArrowRight size={11} />}
                                                </span>
                                            ) : c.passed && c.message ? (
                                                <span className="text-xs text-[#17212B]/50 truncate">{c.message}</span>
                                            ) : null}
                                        </div>
                                        {hint ? (
                                            <div className="mt-2 ml-6 flex items-start gap-2 text-xs text-[#17212B]/70" data-testid={`check-hint-${c.key}`}>
                                                <Info size={12} className="mt-0.5 flex-shrink-0 text-[#0F766E]" />
                                                <span>{hint}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                                const testId = `check-${c.key}`;
                                // Fill-info → open inline dialog with exact missing fields.
                                if (!c.passed && fix?.action === "fill_info") {
                                    return (
                                        <li key={c.key} data-testid={testId}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFillMissing(fix.missing_inputs || []);
                                                    setFillHint(fix.missing_input_hint || "");
                                                    setFillOpen(true);
                                                }}
                                                className="w-full text-left hover:bg-[#F5F8FA] rounded-md transition-colors"
                                                data-testid={`check-fix-${c.key}`}
                                            >
                                                {inner}
                                            </button>
                                        </li>
                                    );
                                }
                                // Photo → inline uploader
                                if (!c.passed && fix?.action === "upload_photo") {
                                    return (
                                        <li key={c.key} data-testid={testId}>
                                            <label
                                                className="block cursor-pointer hover:bg-[#F5F8FA] rounded-md transition-colors"
                                                data-testid={`check-fix-${c.key}`}
                                            >
                                                {inner}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="sr-only"
                                                    disabled={uploadingPhoto}
                                                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                                                    data-testid="checklist-photo-input"
                                                />
                                            </label>
                                        </li>
                                    );
                                }
                                // Deep link
                                if (!c.passed && fix?.to && clickable) {
                                    return (
                                        <li key={c.key} data-testid={testId}>
                                            <Link
                                                to={fix.to}
                                                className="block hover:bg-[#F5F8FA] rounded-md transition-colors"
                                                data-testid={`check-fix-${c.key}`}
                                            >
                                                {inner}
                                            </Link>
                                        </li>
                                    );
                                }
                                // Hint-only (AI-heal rows in non-editable statuses) — click triggers
                                // the same Regenerate-with-AI action as the top-level button. If
                                // the fix has a `secondary` action (typically "Edit / add more info"),
                                // render it as a second button so BOTH remedies are visible.
                                if (!c.passed && fix?.hint_only && fix?.ai) {
                                    return (
                                        <li key={c.key} data-testid={testId}>
                                            <div className="flex items-center justify-between gap-3 py-3 px-3">
                                                <button
                                                    type="button"
                                                    onClick={regenerateAi}
                                                    disabled={regenBusy}
                                                    className="flex items-center gap-2 min-w-0 flex-1 text-left disabled:opacity-60"
                                                    data-testid={`check-fix-${c.key}`}
                                                >
                                                    <XCircle size={16} className="text-[#DC2626] flex-shrink-0" />
                                                    <span className="text-sm text-[#17212B]">{c.label}</span>
                                                    <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#0F766E] hover:underline flex-shrink-0">
                                                        <Sparkles size={11} className={regenBusy ? "animate-pulse" : ""} />
                                                        {regenBusy ? "Regenerating…" : fix.label}
                                                    </span>
                                                </button>
                                                {fix.secondary ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFillMissing(fix.secondary.missing_inputs || []);
                                                            setFillHint(fix.secondary.missing_input_hint || "");
                                                            setFillOpen(true);
                                                        }}
                                                        className="text-xs font-semibold text-[#123B5D] hover:underline inline-flex items-center gap-1 flex-shrink-0 border-l border-[#123B5D]/10 pl-3"
                                                        data-testid={`check-secondary-${c.key}`}
                                                    >
                                                        <Pencil size={11} />
                                                        {fix.secondary.label}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </li>
                                    );
                                }
                                // Passive (payment/approval): render with explainer link but no click
                                if (!c.passed && fix?.passive && fix?.to) {
                                    return (
                                        <li key={c.key} data-testid={testId}>
                                            <div className="flex items-center justify-between gap-3 py-3 px-3">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <XCircle size={16} className="text-[#D97706] flex-shrink-0" />
                                                    <span className="text-sm text-[#17212B]">{c.label}</span>
                                                </div>
                                                <Link
                                                    to={fix.to}
                                                    className="text-xs font-semibold text-[#0F766E] hover:underline inline-flex items-center gap-1"
                                                    data-testid={`check-fix-${c.key}`}
                                                >
                                                    {fix.label} <ArrowRight size={11} />
                                                </Link>
                                            </div>
                                        </li>
                                    );
                                }
                                // Non-fixable / already-passed
                                return (
                                    <li key={c.key} data-testid={testId}>
                                        {inner}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    {uploadingPhoto && (
                        <div className="mt-2 text-xs text-[#17212B]/60 inline-flex items-center gap-1">
                            <Upload size={12} className="animate-pulse" /> Uploading photo…
                        </div>
                    )}
                </section>

                {/* Templates */}
                <section className="mt-8" data-testid="publish-templates-section">
                    <TemplateGallery />
                </section>

                {/* Publish button — receives publishRef so the success banner
                    scroll target lands here. When the checklist just turned
                    all-green (justPassed), the CTA gets a ring highlight so
                    the doctor can't miss it. */}
                <section
                    ref={publishRef}
                    className={
                        "mt-8 flex flex-wrap items-center gap-3 scroll-mt-20 rounded-xl transition-all " +
                        (canPublish && justPassed
                            ? "p-4 ring-4 ring-[#15803D]/40 bg-[#15803D]/5 animate-pulse-once"
                            : "")
                    }
                    data-testid="publish-cta-section"
                >
                    <button
                        className={
                            "ddi-teal-btn text-base inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed " +
                            (canPublish && justPassed ? "shadow-lg shadow-[#15803D]/40 ring-2 ring-[#15803D]" : "")
                        }
                        disabled={!canPublish || busy}
                        onClick={publish}
                        data-testid="publish-publish-btn"
                    >
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                        {isPublished ? "Republish (new version)" : "Publish now"}
                    </button>
                    {canPublish && !isPublished ? (
                        <div className="text-sm text-[#15803D] font-semibold inline-flex items-center gap-1">
                            <CheckCircle2 size={14} /> Ready to go live
                        </div>
                    ) : null}
                    {!canPublish && !isPublished ? (
                        <div className="text-xs text-[#17212B]/60">
                            {doctor?.profile_status === "published"
                                ? "Already live. Editing sends changes for admin re-review (Phase 5)."
                                : "Complete all checklist items to enable publish."}
                        </div>
                    ) : null}
                </section>

                {/* Versions — compare + restore */}
                <section className="mt-10">
                    <PublicationVersions apiPrefix="/me" onRestore={load} />
                </section>

                <section className="mt-10">
                    <h2 className="text-base font-semibold text-[#123B5D]">
                        SEO &amp; Google
                    </h2>
                    <div className="mt-3 ddi-card p-4 text-sm text-[#17212B]/80">
                        <p>
                            Once published, your page is included in the platform sitemap and can be discovered by search engines. We do not (and cannot) guarantee any specific search ranking.
                        </p>
                        <ul className="mt-2 list-disc pl-5 text-[#17212B]/70">
                            <li>Verify your ownership in Google Search Console using your domain.</li>
                            <li>Submit your public URL directly under URL inspection for faster indexing.</li>
                            <li>Add your public URL to your prescription slips, business cards, and Instagram bio.</li>
                        </ul>
                    </div>
                </section>
            </div>
            <ContentFillDialog
                open={fillOpen}
                onOpenChange={setFillOpen}
                missingInputs={fillMissing}
                hint={fillHint}
                onSaved={async (result) => {
                    await load();
                    if (result?.should_regenerate) {
                        // Sufficient input — close dialog and auto-fire the
                        // regenerate. Doctor sees the checklist spinner flip
                        // to green without another click.
                        setFillOpen(false);
                        setTimeout(() => regenerateAi(), 400);
                    }
                    // else: dialog stays open; the child renders an inline
                    // "still not enough" warning surfaced via its own state.
                }}
            />
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="max-w-2xl" data-testid="ai-error-details-dialog">
                    <DialogHeader>
                        <DialogTitle>Technical details</DialogTitle>
                        <DialogDescription>
                            The raw error is stored on the AI job record for admins. This is what came back:
                        </DialogDescription>
                    </DialogHeader>
                    <pre className="whitespace-pre-wrap break-words text-xs bg-[#F5F8FA] border border-[#123B5D]/10 rounded-md p-3 max-h-96 overflow-y-auto text-[#17212B]" data-testid="ai-error-details-text">
                        {detailsText}
                    </pre>
                </DialogContent>
            </Dialog>
        </DoctorShell>
    );
}


/* ============================================================================
 * ContentFillDialog — inline "Fill this information" panel that opens right on
 * the Publishing checklist. Writes exactly the fields the AI needs
 * (biography_notes, education, experience) via POST /me/content-fill, then
 * hands off to the parent's onSaved(result) which auto-triggers a regenerate.
 * ============================================================================
 */
function ContentFillDialog({ open, onOpenChange, missingInputs, hint, onSaved }) {
    const nav = useNavigate();
    const [bio, setBio] = useState("");
    const [edu, setEdu] = useState({ degree: "", institution: "", year: "" });
    const [exp, setExp] = useState({ role: "", organization: "", from_year: "", to_year: "", description: "" });
    const [spec, setSpec] = useState("");
    const [saving, setSaving] = useState(false);
    // When the backend accepts the write but reports the input is STILL
    // insufficient (should_regenerate=false), we surface that inline instead
    // of closing the dialog + firing a doomed AI job.
    const [insufficientMsg, setInsufficientMsg] = useState("");
    const [wizardErrors, setWizardErrors] = useState([]);
    const [qualityIssues, setQualityIssues] = useState([]);

    // Reset the form each time the dialog is opened so previous input
    // doesn't linger between different failing rows.
    useEffect(() => {
        if (open) {
            setBio("");
            setEdu({ degree: "", institution: "", year: "" });
            setExp({ role: "", organization: "", from_year: "", to_year: "", description: "" });
            setSpec("");
            setInsufficientMsg("");
            setWizardErrors([]);
            setQualityIssues([]);
        }
    }, [open]);

    const needsBio = missingInputs.includes("biography_notes") || missingInputs.includes("patient_info");
    const needsEdu = missingInputs.includes("education");
    const needsExp = missingInputs.includes("experience");
    const needsSpec = missingInputs.includes("primary_specialization") ||
                      missingInputs.includes("sub_specializations") ||
                      missingInputs.includes("areas_of_interest") ||
                      missingInputs.includes("procedures_offered");
    // If dialog is opened with NO recognized inputs (defensive against future
    // diagnostic shapes we haven't taught it about), fall back to bio+edu+exp.
    const noRecognizedInputs = !needsBio && !needsEdu && !needsExp && !needsSpec;
    const showBio = needsBio || noRecognizedInputs;
    const showEdu = needsEdu || noRecognizedInputs;
    const showExp = needsExp || noRecognizedInputs;

    // Client-side quality gates mirroring the backend heuristics — prevents
    // enabling Save on inputs the server will just reject.
    const bioMeaningful = bio.replace(/[^A-Za-z0-9\u00C0-\uFFFF]/g, "").length;
    const bioTooShort = showBio && bio.trim().length > 0 && bioMeaningful < 40;
    const eduMeaningful =
        edu.degree.replace(/[^A-Za-z0-9\u00C0-\uFFFF]/g, "").length >= 3 &&
        edu.institution.replace(/[^A-Za-z0-9\u00C0-\uFFFF]/g, "").length >= 3;
    const eduLooksJunk = showEdu && (edu.degree.trim().length > 0 || edu.institution.trim().length > 0) && !eduMeaningful;
    const expMeaningful =
        exp.role.replace(/[^A-Za-z0-9\u00C0-\uFFFF]/g, "").length >= 3 &&
        exp.organization.replace(/[^A-Za-z0-9\u00C0-\uFFFF]/g, "").length >= 3;
    const specMeaningful = needsSpec && spec.trim().length >= 3;

    const canSave =
        (showBio && bioMeaningful >= 40) ||
        (showEdu && eduMeaningful) ||
        (showExp && expMeaningful) ||
        specMeaningful;

    async function save() {
        setSaving(true);
        setInsufficientMsg("");
        setWizardErrors([]);
        setQualityIssues([]);
        try {
            const body = {};
            if (showBio && bio.trim() && bioMeaningful >= 40) body.biography_notes = bio.trim();
            if (showEdu && eduMeaningful) body.education = [edu];
            if (showExp && expMeaningful) body.experience = [exp];
            if (needsSpec && specMeaningful) body.primary_specialization = spec.trim();
            const { data } = await api.post("/me/content-fill", body);
            if (data.should_regenerate) {
                toast.success("Information saved", {
                    description: "Starting AI regeneration…",
                    duration: 4000,
                });
                onSaved?.(data);   // parent will close dialog + fire regenerate
            } else {
                // Server accepted the write but says it still isn't enough
                // grounding for the AI. Keep dialog open, warn inline with
                // the SPECIFIC reason (wizard prerequisites vs junk data).
                const errs = Array.isArray(data.wizard_errors) ? data.wizard_errors : [];
                const issues = Array.isArray(data.quality_issues) ? data.quality_issues : [];
                setWizardErrors(errs);
                setQualityIssues(issues);
                if (errs.length) {
                    setInsufficientMsg(
                        "Your onboarding wizard still has required fields empty (see below). The AI can't produce content until those are set — open the wizard to finish them.",
                    );
                } else if (issues.length) {
                    setInsufficientMsg(
                        "Saved, but some fields look like placeholder text — please clean them up (see below) and Save again.",
                    );
                } else {
                    setInsufficientMsg(
                        "यह जानकारी अभी भी बहुत छोटी है — इस डेटा से AI पूरी biography नहीं लिख पाएगा. Please add more detail (2–3 full sentences OR an education + experience entry) and Save again.",
                    );
                }
                toast.warning("Add a bit more detail", {
                    description: "Saved, but not enough context for the AI yet.",
                    duration: 5000,
                });
                onSaved?.(data);
            }
        } catch (err) {
            toast.error(errMsg(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="content-fill-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[#123B5D] text-xl">Fill this information</DialogTitle>
                    <DialogDescription>{hint || "Add the details below and we'll regenerate your content automatically."}</DialogDescription>
                </DialogHeader>

                <div className="space-y-5 py-2">
                    {needsSpec ? (
                        <div>
                            <Label htmlFor="fill-spec" className="text-[#123B5D] font-semibold">Primary specialisation</Label>
                            <p className="text-xs text-[#17212B]/60 mt-1 mb-2">
                                Your main clinical specialisation (e.g. &ldquo;Cardiology&rdquo;, &ldquo;Dermatology&rdquo;, &ldquo;Orthopaedic Surgery&rdquo;). The AI uses this to compose your SEO title, meta description and services section.
                            </p>
                            <Input
                                id="fill-spec"
                                value={spec}
                                onChange={(e) => setSpec(e.target.value)}
                                placeholder="e.g. Cardiology"
                                data-testid="fill-spec-input"
                            />
                        </div>
                    ) : null}

                    {showBio ? (
                        <div>
                            <Label htmlFor="fill-bio" className="text-[#123B5D] font-semibold">Biography notes</Label>
                            <p className="text-xs text-[#17212B]/60 mt-1 mb-2">
                                2–3 sentences about your practice — years of experience, main focus areas, why patients come to you. The AI will polish this into a full biography and summary. Minimum ~40 meaningful characters.
                            </p>
                            <Textarea
                                id="fill-bio"
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                rows={5}
                                placeholder="e.g. Dr. Sharma is a cardiologist with 15 years of experience in interventional cardiology. Focused on preventive care and complex angioplasty. Consults at Fortis Hospital and Manas Heart Care Clinic in Pune."
                                data-testid="fill-bio-input"
                            />
                            {bioTooShort ? (
                                <div className="mt-2 text-xs text-[#D97706] flex items-start gap-2" data-testid="fill-bio-warning">
                                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                                    <span>Too short ({bioMeaningful} chars) — the AI needs at least 40 meaningful characters. 2–3 sentences works best.</span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {showEdu ? (
                        <div>
                            <Label className="text-[#123B5D] font-semibold">Add an education entry</Label>
                            <p className="text-xs text-[#17212B]/60 mt-1 mb-2">Degree, institution and year — the AI uses these to ground your biography. Each field needs at least 3 real characters.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <Input placeholder="Degree (MBBS, MD…)" value={edu.degree} onChange={(e) => setEdu({ ...edu, degree: e.target.value })} data-testid="fill-edu-degree" />
                                <Input placeholder="Institution" value={edu.institution} onChange={(e) => setEdu({ ...edu, institution: e.target.value })} data-testid="fill-edu-institution" />
                                <Input placeholder="Year (e.g. 2010)" value={edu.year} onChange={(e) => setEdu({ ...edu, year: e.target.value })} data-testid="fill-edu-year" />
                            </div>
                            {eduLooksJunk ? (
                                <div className="mt-2 text-xs text-[#D97706] flex items-start gap-2" data-testid="fill-edu-warning">
                                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                                    <span>Degree or institution is too short. Please enter the full names — the AI will treat 1–2 character entries as placeholder data and refuse to write content.</span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {showExp ? (
                        <div>
                            <Label className="text-[#123B5D] font-semibold">Add an experience entry</Label>
                            <p className="text-xs text-[#17212B]/60 mt-1 mb-2">Role, hospital / clinic, and years — describe your day-to-day scope in one line if you can. Role and organisation each need at least 3 characters.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <Input placeholder="Role (Consultant, Senior…)" value={exp.role} onChange={(e) => setExp({ ...exp, role: e.target.value })} data-testid="fill-exp-role" />
                                <Input placeholder="Organization" value={exp.organization} onChange={(e) => setExp({ ...exp, organization: e.target.value })} data-testid="fill-exp-organization" />
                                <Input placeholder="From year (e.g. 2015)" value={exp.from_year} onChange={(e) => setExp({ ...exp, from_year: e.target.value })} data-testid="fill-exp-from" />
                                <Input placeholder="To year (blank = ongoing)" value={exp.to_year} onChange={(e) => setExp({ ...exp, to_year: e.target.value })} data-testid="fill-exp-to" />
                            </div>
                            <Textarea placeholder="One-line description (optional)" value={exp.description} onChange={(e) => setExp({ ...exp, description: e.target.value })} className="mt-2" rows={2} data-testid="fill-exp-description" />
                        </div>
                    ) : null}

                    {/* Server-reported wizard prerequisites — surface them
                        with a direct wizard link so the doctor can't get
                        stuck in a save-loop that never triggers a job. */}
                    {wizardErrors.length ? (
                        <div className="rounded-md border border-[#DC2626]/30 bg-[#DC2626]/5 p-3" data-testid="fill-wizard-errors">
                            <div className="text-xs font-semibold text-[#DC2626] uppercase tracking-wider mb-1">Wizard still incomplete</div>
                            <ul className="text-xs text-[#17212B]/80 list-disc pl-4 space-y-0.5">
                                {wizardErrors.slice(0, 6).map((e) => <li key={e}>{e}</li>)}
                            </ul>
                            <button
                                type="button"
                                onClick={() => { onOpenChange(false); nav("/doctor/onboarding"); }}
                                className="mt-2 text-xs font-semibold text-[#0F766E] hover:underline inline-flex items-center gap-1"
                                data-testid="fill-goto-wizard"
                            >
                                Open onboarding wizard <ArrowRight size={11} />
                            </button>
                        </div>
                    ) : null}

                    {qualityIssues.length ? (
                        <div className="rounded-md border border-[#D97706]/30 bg-[#D97706]/5 p-3" data-testid="fill-quality-issues">
                            <div className="text-xs font-semibold text-[#D97706] uppercase tracking-wider mb-1">Placeholder-looking data detected</div>
                            <ul className="text-xs text-[#17212B]/80 list-disc pl-4 space-y-0.5">
                                {qualityIssues.slice(0, 4).map((e) => <li key={e}>{e}</li>)}
                            </ul>
                        </div>
                    ) : null}
                </div>

                <DialogFooter>
                    {insufficientMsg ? (
                        <div className="mr-auto flex items-start gap-2 text-xs text-[#DC2626] max-w-md" data-testid="fill-insufficient-warning">
                            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                            <span>{insufficientMsg}</span>
                        </div>
                    ) : null}
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="fill-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving || !canSave}
                        className="bg-[#123B5D] hover:bg-[#0F766E] text-white"
                        data-testid="fill-save-btn"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                        Save &amp; generate
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
